import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useAccounts } from "../lib/account";
import { accountCurrency, formatMoney, SUPPORTED_CURRENCIES } from "../lib/currency";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Trash, Lightning, CheckCircle, PencilSimple, Check, X } from "@phosphor-icons/react";
import MtStatementImport from "../components/MtStatementImport";

export default function Settings() {
  const { user, refresh } = useAuth();
  const { accounts, reload, setActive, activeId } = useAccounts();
  const [displayName, setDisplayName] = useState("");
  const [newAcc, setNewAcc] = useState({ name: "", broker: "", starting_balance: "", currency: "INR" });
  const [templates, setTemplates] = useState([]);
  // Inline-edit state for the Trading Accounts list. `editingId` is the
  // account.id currently being edited (null = view mode); `editDraft`
  // holds the working copy so cancel restores cleanly.
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", broker: "", starting_balance: "" });

  useEffect(() => {
    if (user) setDisplayName(user.name || "");
  }, [user]);

  const loadTemplates = async () => {
    try {
      const { data } = await api.get("/trades/my/templates");
      setTemplates(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadTemplates(); }, []);

  const save = async () => {
    await api.put("/settings", {
      whatsapp_number: user?.settings?.whatsapp_number || "",
      whatsapp_daily_report: false,
      display_name: displayName,
    });
    await refresh();
    toast.success("Settings saved");
  };

  const addAcc = async () => {
    if (!newAcc.name) return toast.error("Account name required");
    try {
      await api.post("/accounts", {
        name: newAcc.name,
        broker: newAcc.broker,
        starting_balance: parseFloat(newAcc.starting_balance || "0"),
        currency: newAcc.currency || "INR",
      });
      toast.success("Account added");
      setNewAcc({ name: "", broker: "", starting_balance: "", currency: "INR" });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const delAcc = async (id) => {
    if (accounts.length <= 1) return toast.error("Keep at least one account");
    if (!window.confirm("Delete account and all its trades?")) return;
    await api.delete(`/accounts/${id}`);
    toast.success("Deleted");
    if (id === activeId && accounts[0]) setActive(accounts[0].id);
    reload();
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setEditDraft({
      name: a.name || "",
      broker: a.broker || "",
      starting_balance: String(a.starting_balance ?? ""),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({ name: "", broker: "", starting_balance: "" });
  };

  const saveEdit = async (id) => {
    const name = editDraft.name.trim();
    if (!name) return toast.error("Account name required");
    try {
      await api.patch(`/accounts/${id}`, {
        name,
        broker: editDraft.broker,
        starting_balance: parseFloat(editDraft.starting_balance || "0"),
      });
      toast.success("Account updated");
      cancelEdit();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const delTemplate = async (fp) => {
    if (!window.confirm("Delete this learned CSV template?")) return;
    await api.delete(`/trades/my/templates/${fp}`);
    toast.success("Template deleted");
    loadTemplates();
  };

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-8">
      <div>
        <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Preferences</p>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Settings</h1>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Profile</p>
        <div>
          <Label>Display name</Label>
          <Input value={displayName} onChange={e => setDisplayName(e.target.value)} data-testid="settings-name" />
        </div>
        <div>
          <Button onClick={save} className="rounded-full" data-testid="save-settings-btn">Save</Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 space-y-3">
        <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Broker Auto-Sync</p>
        <p className="text-sm text-muted-foreground">
          Connect Binance, CoinDCX, or Delta Exchange India with a <b>read-only</b> API key. Every night at 3&nbsp;AM UTC we pull the last 24&nbsp;hours of fills into your journal — no copy-paste. Keys are Fernet-encrypted at rest.
        </p>
        <a href="/settings/brokers" data-testid="link-brokers-settings"
           className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:opacity-90">
          Manage connected brokers →
        </a>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Trading Accounts</p>
        <div className="space-y-2">
          {accounts.map(a => {
            const isEditing = editingId === a.id;
            return (
              <div key={a.id} className="flex items-center justify-between px-4 py-3 rounded-md bg-secondary/40 border border-border" data-testid={`acc-row-${a.id}`}>
                {isEditing ? (
                  <>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2 mr-2">
                      <Input
                        placeholder="Account name"
                        value={editDraft.name}
                        onChange={e => setEditDraft({ ...editDraft, name: e.target.value })}
                        data-testid={`edit-acc-name-${a.id}`}
                      />
                      <Input
                        placeholder="Broker"
                        value={editDraft.broker}
                        onChange={e => setEditDraft({ ...editDraft, broker: e.target.value })}
                        data-testid={`edit-acc-broker-${a.id}`}
                      />
                      <Input
                        placeholder="Starting balance"
                        type="number"
                        value={editDraft.starting_balance}
                        onChange={e => setEditDraft({ ...editDraft, starting_balance: e.target.value })}
                        data-testid={`edit-acc-balance-${a.id}`}
                      />
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" onClick={() => saveEdit(a.id)} data-testid={`save-acc-${a.id}`}>
                        <Check size={16} className="text-emerald-500" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={cancelEdit} data-testid={`cancel-acc-${a.id}`}>
                        <X size={16} />
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <p className="font-medium">
                        {a.name}
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-mono">
                          {accountCurrency(a)}
                        </span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {a.broker || "—"} · Starting {formatMoney(a.starting_balance || 0, accountCurrency(a))}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => startEdit(a)} data-testid={`edit-acc-${a.id}`}>
                        <PencilSimple size={16} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => delAcc(a.id)} data-testid={`delete-acc-${a.id}`}>
                        <Trash size={16} />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 pt-2">
          <Input placeholder="Account name" value={newAcc.name} onChange={e => setNewAcc({...newAcc, name: e.target.value})} data-testid="new-acc-name" />
          <Input placeholder="Broker (optional)" value={newAcc.broker} onChange={e => setNewAcc({...newAcc, broker: e.target.value})} data-testid="new-acc-broker" />
          <Input placeholder="Starting balance" type="number" value={newAcc.starting_balance} onChange={e => setNewAcc({...newAcc, starting_balance: e.target.value})} data-testid="new-acc-balance" />
          <Select value={newAcc.currency} onValueChange={v => setNewAcc({...newAcc, currency: v})}>
            <SelectTrigger data-testid="new-acc-currency"><SelectValue placeholder="Currency" /></SelectTrigger>
            <SelectContent>
              {SUPPORTED_CURRENCIES.map(c => (
                <SelectItem key={c} value={c} data-testid={`currency-opt-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={addAcc} className="rounded-full" data-testid="add-account-btn">+ Add Account</Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Learned CSV Templates</p>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Every time you approve a column mapping for an unknown broker CSV, we remember it here so
          future imports of the same shape happen instantly.
        </p>
        {templates.length === 0 && (
          <p className="text-sm text-muted-foreground">No templates yet. Import a CSV to start teaching.</p>
        )}
        {templates.length > 0 && (
          <div className="space-y-2">
            {templates.map(t => (
              <div key={t.fingerprint} className="flex items-center justify-between px-4 py-3 rounded-md bg-secondary/40 border border-border" data-testid={`tpl-${t.fingerprint}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium font-mono">{t.fingerprint}</p>
                    {t.promoted ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">promoted</span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">
                        {t.hits}/5 hits
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {(t.headers || []).slice(0, 5).join(", ")}{(t.headers || []).length > 5 ? "…" : ""}
                  </p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => delTemplate(t.fingerprint)} data-testid={`del-tpl-${t.fingerprint}`}>
                  <Trash size={16} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-2">Reports</p>
        <p className="text-sm">Download daily / weekly / monthly / yearly / all-time P&amp;L PDFs from the Dashboard, or use your browser's Print for a full snapshot.</p>
        <p className="text-xs text-muted-foreground mt-1">Head to the Dashboard → Reports card.</p>
      </div>

      <MtImportCard />

      <MtSyncCard />
    </div>
  );
}

function MtImportCard() {
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState("mt5");

  const openWith = (p) => { setPlatform(p); setOpen(true); };

  return (
    <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-6" data-testid="mt-import-card">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-semibold uppercase tracking-widest mb-2">
            <Lightning size={11} weight="fill" /> One-click import
          </div>
          <h2 className="font-display text-xl font-bold tracking-tight">MetaTrader 4 / 5 statement upload</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Save your account history from MetaTrader as an HTML report, drop the file here —
            we detect every closed trade and import them instantly. Works with FTMO, MyForexFunds,
            Exness, IC Markets, XM and every other MT4/MT5 broker or prop firm.
          </p>
          <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><CheckCircle size={12} className="text-emerald-500" /> No column mapping</span>
            <span className="flex items-center gap-1"><CheckCircle size={12} className="text-emerald-500" /> No EA install</span>
            <span className="flex items-center gap-1"><CheckCircle size={12} className="text-emerald-500" /> No ports/firewall</span>
            <span className="flex items-center gap-1"><CheckCircle size={12} className="text-emerald-500" /> Auto de-duplication</span>
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <Button
          className="rounded-full"
          onClick={() => openWith("mt5")}
          data-testid="open-mt5-import">
          <Lightning size={14} weight="fill" className="mr-1.5" /> Import MT5 statement
        </Button>
        <Button
          variant="outline"
          className="rounded-full"
          onClick={() => openWith("mt4")}
          data-testid="open-mt4-import">
          Import MT4 statement
        </Button>
      </div>

      <MtStatementImport
        open={open}
        onOpenChange={setOpen}
        defaultPlatform={platform}
      />
    </div>
  );
}

function MtSyncCard() {
  const [token, setToken] = useState("");
  const [status, setStatus] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/mt/webhook`;

  useEffect(() => {
    api.get("/mt/token").then(r => setToken(r.data.token)).catch(() => {});
    api.get("/mt/status").then(r => setStatus(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const regenerate = async () => {
    if (!window.confirm("Rotate the sync token? Your currently-installed EA will stop working until you update it with the new token.")) return;
    try {
      const { data } = await api.post("/mt/regenerate");
      setToken(data.token); setShowToken(true);
      toast.success("New token generated");
    } catch { toast.error("Failed to rotate"); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };
  const mask = token ? `${token.slice(0, 6)}${"•".repeat(20)}${token.slice(-4)}` : "—";

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="mt-sync-card">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-2">Advanced: Expert-Advisor live sync</p>
          <p className="text-sm">Prefer real-time streaming instead of statement uploads? Install our EA in MetaTrader and every closed trade posts here automatically. Recommended only if you leave MT running 24/7.</p>
        </div>
        {status?.connected && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-500 text-[10px] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> CONNECTED
          </span>
        )}
      </div>

      <div className="mt-4 grid md:grid-cols-2 gap-3">
        <div className="rounded-lg border border-border/60 bg-secondary/40 p-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Your sync token</p>
          <div className="mt-1 flex items-center gap-2">
            <code className="flex-1 text-xs font-mono truncate" data-testid="mt-token-value">{showToken ? token : mask}</code>
            <Button variant="ghost" size="sm" onClick={() => setShowToken(v => !v)}>{showToken ? "Hide" : "Show"}</Button>
            <Button variant="ghost" size="sm" onClick={() => copy(token)} data-testid="mt-token-copy">Copy</Button>
          </div>
        </div>
        <div className="rounded-lg border border-border/60 bg-secondary/40 p-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Webhook URL</p>
          <div className="mt-1 flex items-center gap-2">
            <code className="flex-1 text-xs font-mono truncate">{webhookUrl}</code>
            <Button variant="ghost" size="sm" onClick={() => copy(webhookUrl)} data-testid="mt-webhook-copy">Copy</Button>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/mt/ea/mq5`} download="TradelogSync.mq5" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90" data-testid="download-mq5">
          Download MT5 EA
        </a>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/mt/ea/mq4`} download="TradelogSync.mq4" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-secondary text-foreground text-xs font-semibold hover:bg-secondary/80" data-testid="download-mq4">
          Download MT4 EA
        </a>
        <button onClick={regenerate} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-secondary text-foreground text-xs font-semibold hover:bg-secondary/80" data-testid="mt-regenerate">
          Rotate token
        </button>
      </div>

      <details className="mt-4 rounded-lg border border-border/60 bg-secondary/30 p-3 text-xs">
        <summary className="cursor-pointer font-semibold text-sm">Setup guide — 5 steps</summary>
        <ol className="mt-3 space-y-2 list-decimal list-inside text-muted-foreground">
          <li>Download the EA file above (MT5 = .mq5, MT4 = .mq4) and save it.</li>
          <li>Open MetaTrader → File → Open Data Folder → <code className="bg-secondary px-1 rounded">MQL5/Experts</code> (or <code>MQL4/Experts</code> for MT4) → drop the file in.</li>
          <li>In MetaTrader → Tools → Options → Expert Advisors → check <b>Allow WebRequest for the following URLs</b> and add: <code className="bg-secondary px-1 rounded">https://thetradlog.com</code></li>
          <li>Restart MetaTrader → Navigator → Expert Advisors → drag <b>TradelogSync</b> onto any chart.</li>
          <li>In the popup, paste your sync token above and click OK. You should see <i>"Tradelog: sync started"</i> in the Experts tab. New closed trades will stream in automatically every 60 seconds.</li>
        </ol>
      </details>

      {status && (
        <div className="mt-3 text-[11px] text-muted-foreground flex flex-wrap gap-4">
          <span>Last sync: <b>{status.last_sync_at ? new Date(status.last_sync_at).toLocaleString() : "—"}</b></span>
          <span>Trades synced: <b>{status.total_trades || 0}</b></span>
          {status.last_source && <span>Source: <b>{status.last_source.toUpperCase()}</b></span>}
        </div>
      )}
    </div>
  );
}
